import React, { useState } from 'react';
import { DragEndEvent } from '@dnd-kit/core';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ClickableIcon, ConfirmDialog, ErrorResponse, Spinner, Toolbar } from '~components';
import { DndWrapper, DraggableItem, DroppableArea } from '~components/drag-and-drop';
import { SearchBar } from '~components/form-fields/search';
import Icon, {
  faChevronDown,
  faChevronUp,
  faGripLines,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from '~components/icon';

import { CarePlan, CarePlanNeed, NeedPriority } from '~clients/models';

import needsSearchFields from '~clients/forms/care-plans/needsSearch';

import {
  useDeleteCarePlanNeedMutation,
  useGetCarePlanNeedsQuery,
  useReorderCarePlanNeedsMutation,
} from '~clients/actions/carePlans';

import { bySequence, reorderByDrop } from '../reorder';
import NeedFormModal from './NeedFormModal';
import PriorityBadge from './PriorityBadge';

interface ComponentProps {
  plan: CarePlan;
}

/**
 * Assessed Needs tab — lists the plan's CarePlanNeed records with priority badges,
 * inline add/edit/delete, and up/down reordering (persisted via the needs sort-order
 * endpoint). A toolbar carries the add action and a search toggle; the search bar
 * filters the list by priority (client-side). Shown only for sah_ongoing plans; the
 * tab shell handles that gating.
 */
const AssessedNeedsTab: React.FC<ComponentProps> = ({ plan }) => {
  const { data, isLoading, error } = useGetCarePlanNeedsQuery({ planId: plan.id });
  const [deleteNeed, { isLoading: isDeleting, error: deleteError, reset: resetDelete }] =
    useDeleteCarePlanNeedMutation();
  const [reorderNeeds, { isLoading: isReordering, error: reorderError, reset: resetReorder }] =
    useReorderCarePlanNeedsMutation();
  const { showToast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CarePlanNeed | undefined>(undefined);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<NeedPriority | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<CarePlanNeed | null>(null);

  const needs = bySequence(data ?? []);
  const visibleNeeds = priorityFilter ? needs.filter((need) => need.priority === priorityFilter) : needs;
  const isSaving = isDeleting || isReordering;
  const saveError = deleteError ?? reorderError;
  // Reordering stays available while filtered — up/down and drag move relative to the
  // visible cards; only an in-flight save blocks it.
  const reorderDisabled = isSaving;

  const openAdd = () => {
    setEditing(undefined);
    setIsModalOpen(true);
  };
  const openEdit = (need: CarePlanNeed) => {
    setEditing(need);
    setIsModalOpen(true);
  };

  const onSearch = (values: Record<string, unknown>) => {
    setPriorityFilter((values.priority as NeedPriority) || undefined);
  };

  const performDelete = async (need: CarePlanNeed) => {
    // Clear any stale reorder error so the shared banner reflects only this delete's outcome
    // (the reorder hook holds its last error until re-triggered — see the combined saveError).
    resetReorder();
    try {
      await deleteNeed(need.id).unwrap();
      showToast('success', 'Assessed need deleted');
    } catch {
      // Surfaced through saveError below.
    }
  };

  const onConfirmDelete = async (confirm: boolean) => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (confirm && target) await performDelete(target);
  };

  // Move sourceId to targetId's position in the full order and persist. Up/down and drag
  // both route through here (target = adjacent visible card / dropped-on card).
  const persistReorder = (sourceId: string, targetId?: string) => {
    if (!targetId || sourceId === targetId) return;
    // Clear any stale delete error so the shared banner reflects only this reorder's outcome.
    resetDelete();
    reorderNeeds({
      planId: plan.id,
      order: reorderByDrop(
        needs.map((need) => need.id),
        sourceId,
        targetId,
      ),
    });
  };

  // Drop a dragged card onto another to move it there (up/down is the keyboard equivalent).
  const handleReorderDragEnd = (event: DragEndEvent) => {
    const sourceId = event.active.data.current?.needId as string | undefined;
    const targetId = event.over ? String(event.over.id) : undefined;
    if (!sourceId) return;
    persistReorder(sourceId, targetId);
  };

  return (
    <div className="section-card-container">
      <Toolbar
        id="care-plan-needs"
        rightCluster={
          <>
            <ClickableIcon
              id="care-plan-needs-search-button"
              dataCy="care-plan-needs-search-button"
              icon={faMagnifyingGlass}
              toolTip="Toggle search"
              ariaLabel="Toggle search"
              onClick={() => setSearchBarOpen((open) => !open)}
            />
            <Button size="sm" color="success" dataCy="care-plan-need-add" onClick={openAdd}>
              <Icon className="me-2" icon={faPlus} />
              New Need
            </Button>
          </>
        }
      />
      <SearchBar
        id="care-plan-needs"
        isOpen={searchBarOpen}
        fields={needsSearchFields}
        initialValues={priorityFilter ? { priority: priorityFilter } : {}}
        onSubmit={onSearch}
        storageKey="care-plan-needs-search"
      />

      {isLoading && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {error !== undefined && <ErrorResponse dataCy="care-plan-needs-error" message={getErrorMessage(error)} />}
      {saveError !== undefined && (
        <ErrorResponse dataCy="care-plan-needs-save-error" message={getErrorMessage(saveError)} />
      )}

      {!isLoading && error === undefined && needs.length === 0 && (
        <p className="text-muted care-plan-needs-empty" data-cy="care-plan-needs-empty">
          No assessed needs recorded.
        </p>
      )}
      {!isLoading && error === undefined && needs.length > 0 && visibleNeeds.length === 0 && (
        <p className="text-muted care-plan-needs-empty" data-cy="care-plan-needs-no-match">
          No assessed needs match the selected priority.
        </p>
      )}

      {!isLoading && error === undefined && visibleNeeds.length > 0 && (
        <DndWrapper canScroll={false} onDragEnd={handleReorderDragEnd}>
          <div className="care-plan-needs-list">
            {visibleNeeds.map((need, visibleIndex) => (
              <DroppableArea key={need.id} id={need.id} className="care-plan-need-droppable">
                {/* 1-based: dnd-kit handles a falsy 0 id unreliably (broke the top card). */}
                <DraggableItem id={visibleIndex + 1} data={{ needId: need.id }} disabled={reorderDisabled}>
                  <div className="care-plan-need-row" data-cy={`care-plan-need-${need.id}`}>
                    <div className="care-plan-need-reorder">
                      <Icon icon={faGripLines} className="care-plan-need-grip" title="Drag to reorder" aria-hidden />
                      {/* stopPropagation so these controls stay clickable (no drag hijack). */}
                      <div
                        className="care-plan-need-reorder-buttons"
                        onPointerDown={(event) => event.stopPropagation()}
                      >
                        <ClickableIcon
                          id={`care-plan-need-up-${need.id}`}
                          dataCy={`care-plan-need-up-${need.id}`}
                          icon={faChevronUp}
                          size="x-small"
                          disabled={reorderDisabled || visibleIndex === 0}
                          toolTip="Move up"
                          ariaLabel={`Move ${need.title || 'need'} up`}
                          onClick={() => persistReorder(need.id, visibleNeeds[visibleIndex - 1]?.id)}
                        />
                        <ClickableIcon
                          id={`care-plan-need-down-${need.id}`}
                          dataCy={`care-plan-need-down-${need.id}`}
                          icon={faChevronDown}
                          size="x-small"
                          disabled={reorderDisabled || visibleIndex === visibleNeeds.length - 1}
                          toolTip="Move down"
                          ariaLabel={`Move ${need.title || 'need'} down`}
                          onClick={() => persistReorder(need.id, visibleNeeds[visibleIndex + 1]?.id)}
                        />
                      </div>
                    </div>
                    <div className="care-plan-need-body">
                      <div className="care-plan-need-heading">
                        {need.title && <span className="fw-bold">{need.title}</span>}
                        {need.priority && <PriorityBadge priority={need.priority} />}
                      </div>
                      <div className="text-muted">{need.description}</div>
                    </div>
                    <div className="care-plan-need-actions" onPointerDown={(event) => event.stopPropagation()}>
                      <ClickableIcon
                        id={`care-plan-need-edit-${need.id}`}
                        dataCy={`care-plan-need-edit-${need.id}`}
                        icon={faPenToSquare}
                        color="secondary"
                        toolTip="Edit need"
                        ariaLabel={`Edit ${need.title || 'need'}`}
                        onClick={() => openEdit(need)}
                      />
                      <ClickableIcon
                        id={`care-plan-need-delete-${need.id}`}
                        dataCy={`care-plan-need-delete-${need.id}`}
                        icon={faTrash}
                        color="danger"
                        disabled={isSaving}
                        toolTip="Delete need"
                        ariaLabel={`Delete ${need.title || 'need'}`}
                        onClick={() => setPendingDelete(need)}
                      />
                    </div>
                  </div>
                </DraggableItem>
              </DroppableArea>
            ))}
          </div>
        </DndWrapper>
      )}

      <NeedFormModal
        key={editing?.id ?? 'new'}
        planId={plan.id}
        need={editing}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
      />
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        useList={false}
        messages={
          <p>
            <strong>Warning:</strong> This assessed need will be deleted.
          </p>
        }
        onClose={onConfirmDelete}
      />
    </div>
  );
};

export default AssessedNeedsTab;
